import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { payments } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await payments.checkStatus(sessionId);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to get updated credit
        const { auth } = await import('@/lib/api');
        const profileRes = await auth.getProfile();
        updateUser(profileRes.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (!sessionId) {
    return (
      <div className="container-wide py-16 text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Invalid Session</h1>
        <p className="text-[#64748B] mb-8">No payment session found.</p>
        <Link to="/dashboard">
          <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
            Go to Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-wide py-16 text-center fade-in">
      {status === 'checking' && (
        <>
          <Loader2 className="w-16 h-16 text-[#002FA7] mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Verifying Payment</h1>
          <p className="text-[#64748B]">Please wait while we confirm your payment...</p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Payment Successful!</h1>
          <p className="text-[#64748B] mb-2">Your payment has been processed successfully.</p>
          {paymentData && (
            <p className="text-lg font-semibold text-[#002FA7] mb-8">
              Amount Paid: ${paymentData.amount?.toFixed(2)}
            </p>
          )}
          <p className="text-sm text-[#64748B] mb-8">
            Your credit balance has been updated. Thank you for your payment!
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/dashboard">
              <Button className="bg-[#002FA7] hover:bg-[#0025a0]" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline">Continue Shopping</Button>
            </Link>
          </div>
        </>
      )}

      {status === 'expired' && (
        <>
          <XCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Payment Session Expired</h1>
          <p className="text-[#64748B] mb-8">Your payment session has expired. Please try again.</p>
          <Link to="/dashboard">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Go to Dashboard
            </Button>
          </Link>
        </>
      )}

      {status === 'timeout' && (
        <>
          <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Payment Processing</h1>
          <p className="text-[#64748B] mb-8">
            Your payment is still being processed. Please check your dashboard in a few minutes.
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Go to Dashboard
            </Button>
          </Link>
        </>
      )}

      {status === 'error' && (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Error</h1>
          <p className="text-[#64748B] mb-8">
            There was an error checking your payment status. Please contact support if the issue persists.
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Go to Dashboard
            </Button>
          </Link>
        </>
      )}
    </div>
  );
};

export default PaymentSuccessPage;
